import { useDashIntl, useDashMessages } from '../../../intl/intl.hooks'
import styled from '@emotion/styled'
import { Button, Card, Pill } from '@retailer-platform/shared-components/src/tds'
import {
  spacing,
  colors,
  fontSize,
  fontWeight,
} from '@retailer-platform/shared-components/src/foundation'
import {
  diffChanges,
  filterDisplayState,
  hasContext,
  humanizeToolName,
  type ChangeRow,
} from './approvalHelpers'
import type { MutationPreview } from '../hooks/useRetailerAgentChat'

interface Props {
  preview: MutationPreview
  onApprove: () => void
  onCancel: () => void
  locked?: boolean
}

// TDS Card.Header only accepts a `header: string` prop (no children); render the
// header layout inline at the top of Card.Body instead.
const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing.X8,
  marginBottom: spacing.X12,
})

const Title = styled.div({
  fontSize: fontSize.X14,
  fontWeight: fontWeight.SEMIBOLD,
})

const TargetLine = styled.div({
  fontSize: fontSize.X12,
  color: colors.GRAYSCALE.X60,
  marginBottom: spacing.X12,
})

const ChangeRowEl = styled.div<{ op: ChangeRow['op'] }>(({ op }) => ({
  display: 'flex',
  gap: spacing.X8,
  padding: `${spacing.X8} ${spacing.X12}`,
  background: colors.GRAYSCALE.X20,
  borderRadius: 6,
  marginBottom: spacing.X4,
  fontSize: fontSize.X14,
  '& .op': {
    fontFamily: 'ui-monospace, Menlo, monospace',
    color:
      op === 'add'
        ? colors.SUCCESS.REGULAR
        : op === 'remove'
          ? colors.DETRIMENTAL.REGULAR
          : colors.GRAYSCALE.X60,
    fontWeight: fontWeight.SEMIBOLD,
  },
}))

const FooterRow = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: spacing.X8,
  marginTop: spacing.X16,
})

const Disclosure = styled.details({
  marginTop: spacing.X8,
  '& summary': {
    cursor: 'pointer',
    fontSize: fontSize.X12,
    color: colors.GRAYSCALE.X70,
  },
  '& pre': {
    fontSize: fontSize.X12,
    color: colors.GRAYSCALE.X70,
    marginTop: spacing.X8,
    overflow: 'auto',
  },
})

function fmtValue(v: unknown): string {
  if (v == null) return '—'
  if (Array.isArray(v)) return v.length === 0 ? '(none)' : v.join(', ')
  return String(v)
}

function renderRow(row: ChangeRow, i: number) {
  const symbol = row.op === 'add' ? '+' : row.op === 'remove' ? '−' : '·'
  return (
    <ChangeRowEl key={i} op={row.op}>
      <span className="op">{symbol}</span>
      <div>
        {row.op === 'change' ? (
          <>
            {row.label}: {fmtValue(row.from)} → {fmtValue(row.to)}
          </>
        ) : (
          row.label
        )}
      </div>
    </ChangeRowEl>
  )
}

const messageIds = {
  write: 'retailerAgent.approval.write' as const,
  idempotentSuffix: 'retailerAgent.approval.idempotentSuffix' as const,
  targetPrefix: 'retailerAgent.approval.targetPrefix' as const,
  showCurrent: 'retailerAgent.approval.showCurrent' as const,
  cancel: 'retailerAgent.approval.cancel' as const,
  approve: 'retailerAgent.approval.approve' as const,
}

export function ApprovalCard({ preview, onApprove, onCancel, locked }: Props) {
  const intl = useDashIntl()
  const messages = useDashMessages(messageIds)
  const changes = diffChanges(preview.beforeState, preview.proposedChanges)
  // beforeState may carry an `email` field — render it as the target context line.
  const targetEmail =
    typeof (preview.beforeState as { email?: unknown }).email === 'string'
      ? (preview.beforeState as { email: string }).email
      : undefined

  return (
    <Card>
      <Card.Body>
        <HeaderRow>
          <Title>
            {intl.formatMessage(
              { id: 'retailerAgent.approval.cardTitle' },
              { toolName: humanizeToolName(preview.toolName) }
            )}
          </Title>
          <Pill color="gray">
            {messages.write}
            {preview.idempotent && messages.idempotentSuffix}
          </Pill>
        </HeaderRow>
        {targetEmail !== undefined && (
          <TargetLine>
            {messages.targetPrefix} <strong>{targetEmail}</strong>
          </TargetLine>
        )}
        {changes.map(renderRow)}
        {hasContext(preview.beforeState) && (
          <Disclosure>
            <summary>{messages.showCurrent}</summary>
            <pre>{JSON.stringify(filterDisplayState(preview.beforeState), null, 2)}</pre>
          </Disclosure>
        )}
        <FooterRow>
          <Button variant="tertiary" onClick={onCancel} disabled={locked}>
            {messages.cancel}
          </Button>
          <Button variant="primary" onClick={onApprove} disabled={locked}>
            {messages.approve}
          </Button>
        </FooterRow>
      </Card.Body>
    </Card>
  )
}
